import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { format } from "date-fns"

interface Slot {
  from: string;
  to: string;
}

interface Props {
  show: boolean;
  slots: Record<string, Slot[]>;
  selectedDate: string;
  setSelectedDate: (v: string) => void;

  slotInfo: string;

  tempSelectedSlot: Slot | null;
  currentActiveSlots: Slot[];
  paginatedSlots: Slot[];

  handleSelectSlot: (slot: Slot) => void;
  handleCancel: () => void;
  handleConfirm: () => void;

  MAX_SLOTS: number;
  setPaginatedSlots: (arr: Slot[]) => void;

  parseSlotInfo: (s: string) => any;
}

const RescheduleModal: React.FC<Props> = ({
  show,
  slots,
  selectedDate,
  setSelectedDate,
  slotInfo,
  tempSelectedSlot,
  currentActiveSlots,
  paginatedSlots,
  handleSelectSlot,
  handleCancel,
  handleConfirm,
  MAX_SLOTS,
  setPaginatedSlots,
  parseSlotInfo,
}) => {
  if (!show) return null;

  return (
    <div className="mt-4 bg-white rounded-lg shadow-sm p-1 mb-6">
      <h3 className="mb-4 text-lg font-bold text-primary-2">Available Slots</h3>
      <div className="flex flex-wrap gap-4">
        <Tabs
          value={selectedDate}
          onValueChange={(val) => {
            setSelectedDate(val)
          }}
          className="w-full"
        >
          <TabsList className="mb-4 h-[unset] w-full justify-center overflow-x-auto rounded-none border-b-2 p-0">
            {Object.keys(slots)
              .filter((slot) => {
                const [day, month, year] = slot.split("-").map(Number);
                const slotDate = new Date(year, month - 1, day);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Parse current booking date to always include it
                const currentBooking = parseSlotInfo(slotInfo);
                const isCurrentBookingDate = currentBooking && slot === currentBooking.date;

                return slotDate >= today || isCurrentBookingDate;
              })
              .sort((a, b) => {
                const todayStr = format(new Date(), "dd-MM-yyyy");

                if (a === todayStr) return -1;
                if (b === todayStr) return 1;

                const [dayA, monthA, yearA] = a.split("-").map(Number);
                const [dayB, monthB, yearB] = b.split("-").map(Number);

                const dateA = new Date(yearA, monthA - 1, dayA);
                const dateB = new Date(yearB, monthB - 1, dayB);

                return dateA.getTime() - dateB.getTime();
              })
              .slice(0, 5)
              .map((slot) => {
                const todayStr = format(new Date(), "dd-MM-yyyy");
                const [day, month, year] = slot.split("-").map(Number);
                const jsDate = new Date(year, month - 1, day);
                const isToday = slot === todayStr;
                const dateStr = isToday ? "Today" : format(jsDate, "d MMM");

                return (
                  <TabsTrigger
                    key={slot}
                    value={slot}
                    className="rounded-t-md !border-0 text-sm font-semibold text-foreground data-[state=active]:bg-primary-2 data-[state=active]:text-primary-foreground transition-colors"
                  >
                    {dateStr}
                  </TabsTrigger>
                )
              })}
          </TabsList>

          <TabsContent value={selectedDate} className="flex flex-col items-center">
            {currentActiveSlots.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 w-full">
                  {paginatedSlots.map((slot) => {
                    const isSelected = tempSelectedSlot &&
                      tempSelectedSlot.from.trim() === slot.from.trim() &&
                      tempSelectedSlot.to.trim() === slot.to.trim();

                    const isOriginalSlot = (() => {
                      const currentBooking = parseSlotInfo(slotInfo);
                      return currentBooking &&
                        selectedDate === currentBooking.date &&
                        currentBooking.slot.from.trim() === slot.from.trim() &&
                        currentBooking.slot.to.trim() === slot.to.trim();
                    })();

                    return (
                      <Button
                        key={slot.from}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "rounded-full border-2 text-xs lowercase hover:border-primary-2 transition-colors",
                          {
                            "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground": isSelected,
                            "border-orange-500 !bg-orange-50 font-bold !text-orange-600": isOriginalSlot && !isSelected,
                          },
                        )}
                        onClick={() => handleSelectSlot(slot)}
                      >
                        {slot.from} - {slot.to}
                        {isOriginalSlot && <span className="ml-1 text-xs">(Current)</span>}
                      </Button>
                    )
                  })}
                </div>

                {/* Fixed "See all slots" button */}
                {currentActiveSlots.length > MAX_SLOTS && paginatedSlots.length < currentActiveSlots.length && (
                  <Button
                    className="mt-7 text-xs font-bold text-primary underline"
                    variant="link"
                    onClick={() => {
                      console.log("Expanding slots from", paginatedSlots.length, "to", currentActiveSlots.length);
                      setPaginatedSlots([...currentActiveSlots]); // Create new array reference
                    }}
                  >
                    See all slots ({currentActiveSlots.length} available)
                  </Button>
                )}

                {(tempSelectedSlot) && (
                  <div className="mt-6 w-full flex justify-center gap-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-gray-300 hover:bg-gray-50"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    {(() => {
                      const currentBooking = parseSlotInfo(slotInfo);
                      const isDifferentSelection = currentBooking && tempSelectedSlot && (
                        selectedDate !== currentBooking.date ||
                        (tempSelectedSlot.from !== currentBooking.slot.from ||
                          tempSelectedSlot.to !== currentBooking.slot.to)
                      );

                      return isDifferentSelection && (
                        <Button
                          size="sm"
                          className="rounded-full bg-primary-2 hover:bg-primary-2/90 shadow-md"
                          onClick={handleConfirm}
                        >
                          Confirm Reschedule
                        </Button>
                      );
                    })()}
                  </div>
                )}
              </>
            ) : (
              <p className="pb-28 text-muted-foreground">
                No available slots for this date
              </p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default RescheduleModal;
